// PostHog initialization. Reads VITE_POSTHOG_KEY + VITE_POSTHOG_HOST from env.
// If the key is missing (local dev without analytics) PostHog is a no-op so
// nothing breaks; calls to capture/identify just discard.
import posthog from 'posthog-js'
import { sanitizeCapture } from '@/lib/trackedPath'

let initialized = false

export function initAnalytics(): void {
  if (initialized) return
  if (typeof window === 'undefined') return
  if ((window as { __PRERENDER_INJECTED?: { isPrerender?: boolean } }).__PRERENDER_INJECTED?.isPrerender) {
    // The prerender browser must not phone home to analytics — every snapshot
    // would otherwise count as a session.
    return
  }
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (!key || key === 'replace-with-real-key') {
    // No-op mode. Calls to posthog.* will warn-and-discard.
    return
  }
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com'
  posthog.init(key, {
    api_host: host,
    capture_pageview: false, // we fire it manually from router.afterEach so SPA navs are tracked
    // Memory-only persistence so we don't write cookies or localStorage
    // without consent. Loses cross-session attribution (a returning user looks
    // like a new one), but keeps the launch defensible under GDPR/PECR
    // without shipping a cookie banner. Switch back to 'localStorage+cookie'
    // once a consent UI is wired up.
    persistence: 'memory',
    autocapture: true,
    disable_session_recording: true, // turn on later via PostHog dashboard if needed
    // Autocaptured events stamp $current_url / $pathname from the address bar: strip the tokens
    // emailed links carry (reset, invitation, unsubscribe) from every event before it is sent.
    before_send: (event) => sanitizeCapture(event),
    // No /flags request: it sends person_properties ($initial_current_url = the raw first URL, so
    // an emailed token) without passing through before_send, and the app uses no feature flags.
    // Remote config (autocapture, heatmaps, web vitals, surveys) still loads; flags,
    // flag-targeted surveys and early-access features are what this turns off.
    // (Not `advanced_disable_flags`: in posthog-js 1.372.9 that also stops remote config.)
    advanced_disable_feature_flags: true,
  })
  initialized = true
}

export function isAnalyticsInitialized(): boolean {
  return initialized
}

export { posthog }
